import { useEffect } from 'react';
import PerfectScrollbar from 'react-perfect-scrollbar';
import {
  Link as RouterLink,
  useLocation,
  matchPath,
} from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  Box,
  Drawer,
  Hidden,
  IconButton,
  List,
  ListSubheader,
  Tooltip,
} from '@material-ui/core';
import {
  Menu as MenuIcon,
  MenuOpen as MenuOpenIcon,
} from '@material-ui/icons';

import Logo from 'components/Logo';
import { parseIfValid } from 'utils/json';
import { hasPermissions } from 'utils/permissions';
import { firstLetter } from 'utils/strings';

import Account from '../TopBar/Account';
import Notifications from '../TopBar/Notifications';
import NavItem from './NavItem';
import useStyles from './styles';
import { items } from './constants/items';
import { canUsePos } from 'utils/pos'; //TODO: Una vez pasada la beta retirar

const company = parseIfValid(localStorage.getItem('company'), false);

// Filtros de visibilidad existentes (sandbox, catálogo, compañía, permisos).
// Se extrae tal cual para poder agrupar por sección sin duplicar reglas.
function filterItems(items) {
  const showByCompany = {};
  const isSandboxAccount =
  parseIfValid(localStorage.getItem('isSandbox'), false) === true;
  const hasPosAccess = canUsePos(); //TODO: Una vez pasada la beta retirar

  let options = items
    .filter((item) => {
      if (typeof item.isSandbox === 'boolean') {
        return item.isSandbox === isSandboxAccount;
      }
      return true;
    })
    .filter((item) => { ////TODO: Una vez pasada la beta retirar
      if (item.requiresCatalog) {
        return hasPosAccess;
      }
      return true;
    }) //TODO: Eliminar hasta aquí
    .filter((item) => (item.href in showByCompany)
      ? showByCompany[item.href].includes(company?.id)
      : true);

  options = options
    .filter((item) => {
      if ([null, undefined, ''].includes(item.module)) {
        return true;
      }

      const permissionName = Array.isArray(item.module)
        ? item.module.map((itemModule) => `${itemModule}.select.list${firstLetter(itemModule)}`)
        : `${item.module}.select.list${firstLetter(item.module)}`;

      return hasPermissions(permissionName, true);
    });

  return options;
}

function renderNavItems({ items, ...rest }) {
  const options = filterItems(items);

  return (
    <List disablePadding>
      {options.reduce(
        (acc, item) => reduceChildRoutes({
          acc,
          item,
          ...rest,
        }),
        [],
      )}
    </List>
  );
}

function reduceChildRoutes({ acc, pathname, item, depth = 0, collapsed, onNeedExpand }) {
  const key = item.title + depth;

  if (item.items) {
    const open = matchPath(pathname, {
      path: item.href,
      exact: false,
    });

    acc.push(
      <NavItem
        collapsed={collapsed}
        depth={depth}
        icon={item.icon}
        key={key}
        info={item.info}
        onNeedExpand={onNeedExpand}
        open={Boolean(open)}
        title={item.title}
      >
        {renderNavItems({
          depth: depth + 1,
          pathname,
          collapsed: false,
          items: item.items.filter(_ => !_.hidden),
        })}
      </NavItem>,
    );
  } else {
    acc.push(
      <NavItem
        collapsed={collapsed}
        depth={depth}
        href={item.href}
        icon={item.icon}
        key={key}
        info={item.info}
        title={item.title}
      />,
    );
  }

  return acc;
}

function NavBar({ openMobile, openMenu, onMobileClose, onExpand }) {
  const classes = useStyles();
  const location = useLocation();

  useEffect(() => {
    if (openMobile && onMobileClose) {
      onMobileClose();
    }
  }, [location.pathname]);

  // Secciones derivadas del campo `section` de cada ítem, ya filtrado por
  // permisos/flags. Una sección sin ítems visibles no se renderiza.
  const navConfig = filterItems(items).reduce((acc, item) => {
    const subheader = item.section || '';
    const group = acc.find((section) => section.subheader === subheader);

    if (group) {
      group.items.push(item);
    } else {
      acc.push({
        subheader,
        items: [item],
      });
    }

    return acc;
  }, []);

  const renderContent = (collapsed) => (
    <Box className={classes.content}>
      {/* Marca + control de colapso (siempre visible arriba) */}
      <Box className={`${classes.brand} ${collapsed ? classes.brandCollapsed : ''}`}>
        {collapsed ? (
          onExpand && (
            <Tooltip title="Expandir menú" placement="right">
              <IconButton
                className={classes.collapseButton}
                onClick={() => onExpand(true)}
                size="small"
                style={{
                  marginLeft: 0,
                }}
              >
                <MenuIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )
        ) : (<>
          <RouterLink to="/" style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            textDecoration: 'none',
            minWidth: 0,
          }}>
            <Logo
              style={{
                display: 'block',
                width: 180,
                height: 42,
                objectFit: 'contain',
                objectPosition: 'left center',
              }}
              variant="imagotipo-horizontal-dark"
            />
          </RouterLink>
          {onExpand && (
            <Hidden mdDown>
              <Tooltip title="Contraer menú" placement="right">
                <IconButton
                  className={classes.collapseButton}
                  onClick={() => onExpand(false)}
                  size="small"
                >
                  <MenuOpenIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Hidden>
          )}
        </>)}
      </Box>

      {/* Navegación (permisos y flags existentes intactos) */}
      <PerfectScrollbar
        className={classes.scroll}
        options={{
          suppressScrollX: true,
        }}
      >
        <Box className={collapsed ? classes.navCollapsed : classes.nav}>
          {navConfig.map((config, index) => (
            <List
              key={index}
              subheader={config.subheader ? (
                <ListSubheader
                  className={classes.subheader}
                  disableGutters
                  disableSticky
                >
                  {collapsed ? (
                    <span className={classes.subheaderDash} />
                  ) : config.subheader}
                </ListSubheader>
              ) : undefined}
            >
              {renderNavItems({
                items: config.items,
                pathname: location.pathname,
                collapsed,
                onNeedExpand: onExpand ? () => onExpand(true) : null,
              })}
            </List>
          ))}
        </Box>
      </PerfectScrollbar>

      {/* Cuenta (menú, modal y permisos existentes) */}
      <Box className={`${classes.accountWrapper} ${classes.account} ${collapsed ? classes.accountCollapsed : ''}`}>
        <Account collapsed={collapsed} variant="sidebar" />
        {!collapsed && process.env.NODE_ENV !== 'production' && (
          <Notifications />
        )}
      </Box>
    </Box>
  );

  return (
    <>
      <Hidden lgUp>
        <Drawer
          anchor="left"
          children={renderContent(false)}
          classes={{
            paper: classes.mobileDrawer,
          }}
          onClose={onMobileClose}
          open={openMobile}
          variant="temporary"
        />
      </Hidden>
      <Hidden mdDown>
        <Drawer
          anchor="left"
          children={renderContent(!openMenu)}
          classes={{
            paper: classes[openMenu
              ? 'desktopDrawer'
              : 'desktopMiniDrawer'],
          }}
          open
          variant="permanent"
        />
      </Hidden>
    </>
  );
}

NavBar.propTypes = {
  onExpand: PropTypes.func,
  onMobileClose: PropTypes.func,
  openMobile: PropTypes.bool,
  openMenu: PropTypes.bool,
};

export default NavBar;
